import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Home as HomeIcon, Layers, Droplets, Image as ImageIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../components/SEO';

const categories = [
  { id: 'alle', name: 'Alle Projekte', icon: <ImageIcon /> },
  { id: 'dach', name: 'Dacharbeiten', icon: <HomeIcon /> },
  { id: 'fassade', name: 'Fassadenarbeiten', icon: <Layers /> },
  { id: 'abdichtung', name: 'Abdichtungsarbeiten', icon: <Droplets /> }
];

// Platzhalter-Projekte mit SEO-optimierten Beschreibungen
const projects = [
  {
    id: 1,
    category: 'dach',
    title: 'Dachsanierung Einfamilienhaus Fürth',
    description: 'Komplette Dachsanierung mit neuer Dämmung und hochwertigen Tonziegeln. Energetische Modernisierung inkl. Dampfsperre und Lattung für optimalen Wärmeschutz.',
    location: 'Fürth',
    year: '2024'
  },
  {
    id: 2,
    category: 'dach',
    title: 'Flachdach Neubau Gewerbe Nürnberg',
    description: 'Professionelle Flachdachabdichtung für Gewerbeobjekt mit hochwertigen EPDM-Bahnen. Inklusive Wärmedämmung und fachgerechter Entwässerung.',
    location: 'Nürnberg',
    year: '2024'
  },
  {
    id: 3,
    category: 'dach',
    title: 'Steildach-Eindeckung Altbau',
    description: 'Denkmalgerechte Dacheindeckung mit traditionellen Biberschwanzziegeln. Erhalt der historischen Optik mit modernen Dämm-Standards.',
    location: 'Fürth',
    year: '2023'
  },
  {
    id: 4,
    category: 'fassade',
    title: 'Fassadensanierung Mehrfamilienhaus',
    description: 'Energetische Fassadensanierung mit WDVS-System und mineralischem Oberputz. Wärmedämmung 16cm für optimale Energieeffizienz und Wertsteigerung.',
    location: 'Erlangen',
    year: '2024'
  },
  {
    id: 5,
    category: 'fassade',
    title: 'Hinterlüftete Fassade Neubau',
    description: 'Moderne hinterlüftete Fassadenkonstruktion mit Faserzementplatten. Langlebige und wartungsarme Lösung mit erstklassiger Optik.',
    location: 'Fürth',
    year: '2023'
  },
  {
    id: 6,
    category: 'fassade',
    title: 'Fassadenrenovierung Altbau',
    description: 'Denkmalpflegerische Fassadenrenovierung mit traditionellen Putztechniken. Instandsetzung historischer Stuckelemente und Farbgestaltung.',
    location: 'Nürnberg',
    year: '2024'
  },
  {
    id: 7,
    category: 'abdichtung',
    title: 'Balkonabdichtung Wohnanlage',
    description: 'Professionelle Flüssigabdichtung für Balkone und Terrassen. Riss-überbrückend und UV-beständig für langanhaltenden Schutz vor Feuchtigkeit.',
    location: 'Fürth',
    year: '2024'
  },
  {
    id: 8,
    category: 'abdichtung',
    title: 'Kellerabdichtung Außen',
    description: 'Nachträgliche Kelleraußenabdichtung mit bituminösen Dickbeschichtungen. Schutz vor drückendem Wasser und Bodenfeuchtigkeit.',
    location: 'Schwabach',
    year: '2023'
  },
  {
    id: 9,
    category: 'abdichtung',
    title: 'Dachterrasse Abdichtung',
    description: 'Hochwertige Abdichtung begehbarer Dachterrasse mit Flüssigkunststoff. Fugenlose Ausführung mit Gefälle und fachgerechten Anschlüssen.',
    location: 'Fürth',
    year: '2024'
  },
  {
    id: 10,
    category: 'dach',
    title: 'Dachfenster-Einbau Wohnhaus',
    description: 'Fachgerechter Einbau von hochwertigen Dachfenstern für optimalen Lichteinfall. Inklusive wärmegedämmter Anschlüsse und Sonnenschutz.',
    location: 'Fürth',
    year: '2023'
  },
  {
    id: 11,
    category: 'fassade',
    title: 'Klinkerfassade Einfamilienhaus',
    description: 'Hochwertige Klinkerfassade mit vorgehängter Konstruktion. Wartungsfrei und langlebig mit erstklassiger Wärmedämmung.',
    location: 'Erlangen',
    year: '2023'
  },
  {
    id: 12,
    category: 'dach',
    title: 'Dachdämmung Altbausanierung',
    description: 'Aufsparrendämmung für Altbau mit ökologischen Dämmstoffen. KfW-förderfähige Lösung für optimale Energieeffizienz und Wohnkomfort.',
    location: 'Nürnberg',
    year: '2024'
  }
];

export const GalleryPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('alle');

  // Scroll to top when page loads
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredProjects = activeCategory === 'alle' 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  return (
    <>
      <SEO 
        title="Projekt-Galerie"
        description="Überzeugen Sie sich von unseren abgeschlossenen Projekten: Dachsanierung, Fassadenarbeiten und Abdichtung in Fürth, Nürnberg und Umgebung. Referenzen des Meisterbetriebs MP Frankendach."
        keywords="Dachdecker Referenzen, Dachsanierung Projekte, Fassadenarbeiten Beispiele, MP Frankendach Galerie"
        canonical="https://mp-frankendach.de/galerie"
      />
      
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <motion.button
              onClick={() => navigate('/')}
              className="flex items-center space-x-1 sm:space-x-2 text-gray-600 hover:text-red-600 transition-colors"
              whileHover={{ x: -5 }}
            >
              <ArrowLeft size={20} />
              <span className="font-semibold text-sm sm:text-base">Zurück</span>
            </motion.button>
            <img
              src="/images/logo.png"
              alt="MP Frankendach Logo"
              className="h-10 sm:h-12 w-auto object-contain"
            />
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-red-950 text-white py-12 sm:py-20 relative overflow-hidden">
        {/* Grain Overlay */}
        <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noise)"/%3E%3C/svg%3E")'
        }}></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6">
              Unsere
              <span className="block bg-gradient-to-r from-red-400 to-red-600 bg-clip-text text-transparent mt-1 sm:mt-2">
                Projekt-Galerie
              </span>
            </h1>
            <p className="text-base sm:text-xl text-gray-300 max-w-3xl mx-auto px-2">
              Überzeugen Sie sich von unserer Expertise durch abgeschlossene Projekte
            </p>
          </motion.div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white border-b border-gray-200 sticky top-14 sm:top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            {categories.map((cat) => (
              <motion.button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all ${
                  activeCategory === cat.id
                    ? 'bg-gradient-to-r from-red-600 to-red-800 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                whileTap={{ scale: 0.95 }}
              >
                {React.cloneElement(cat.icon, { size: 16, className: 'sm:w-5 sm:h-5' })}
                <span className="hidden xs:inline sm:inline">{cat.name}</span>
                <span className="xs:hidden sm:hidden">{cat.name.split(' ')[0]}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-16">
        <motion.div
          layout
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {filteredProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} />
          ))}
        </motion.div>
      </div>
    </div>
    </>
  );
};

const ProjectCard = ({ project, index }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 30 }}
    transition={{ duration: 0.5, delay: index * 0.05 }}
    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
    whileHover={{ y: -10 }}
  >
    {/* Image Placeholder */}
    <div className="relative h-64 bg-gradient-to-br from-gray-200 to-gray-300 overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-500 font-semibold">Projektbild</p>
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
      
      {/* Category Badge */}
      <div className="absolute top-4 left-4 bg-gradient-to-r from-red-600 to-red-800 text-white px-4 py-2 rounded-lg font-semibold text-sm">
        {categories.find(c => c.id === project.category)?.name.replace('arbeiten', '')}
      </div>

      {/* Year Badge */}
      <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1 rounded-lg font-semibold text-sm">
        {project.year}
      </div>
    </div>

    {/* Content */}
    <div className="p-6">
      <h3 className="text-xl font-bold mb-2 text-gray-900 group-hover:text-red-600 transition-colors">
        {project.title}
      </h3>
      <p className="text-gray-600 text-sm mb-4 leading-relaxed">
        {project.description}
      </p>
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span className="flex items-center space-x-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          <span>{project.location}</span>
        </span>
      </div>
    </div>
  </motion.div>
);